import React, { useState, useEffect } from "react";
import { Box, Button, CircularProgress } from "@mui/material";
import { LinkOffRounded, WarningAmberRounded } from "@mui/icons-material";
import { useSearchParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { enqueueSnackbar } from "notistack";
import ConfirmationDialog from "../Common/ConfirmationDialog";
import color from "../../Constants/colors";
import { formatDate } from "../../Helpers/dateHelpers";
import { profileButtonSx, SettingRow } from "./ProfileSection";
import {
  useMicrosoftStatus,
  useConnectMicrosoft,
  useDisconnectMicrosoft,
} from "../../Query/useSharepoint";

const connectFailureKeys: Record<string, string> = {
  admin_approval_required: "profile.microsoftConnectFailedAdminApproval",
  access_denied: "profile.microsoftConnectFailedAccessDenied",
  no_refresh_token: "profile.microsoftConnectFailedNoRefreshToken",
  invalid_state: "profile.microsoftConnectFailedInvalidState",
};

const MicrosoftConnection: React.FC = () => {
  const { t } = useTranslation("profile");
  const [searchParams, setSearchParams] = useSearchParams();
  const { data: microsoftStatus, isLoading } = useMicrosoftStatus();
  const connectMutation = useConnectMicrosoft();
  const disconnectMutation = useDisconnectMicrosoft();
  const [disconnectDialogOpen, setDisconnectDialogOpen] = useState(false);

  useEffect(() => {
    const msConnected = searchParams.get("ms-connected");

    if (msConnected) {
      if (msConnected === "true") {
        enqueueSnackbar(t("profile.microsoftConnectSuccess"), {
          variant: "success",
        });
      } else {
        const reason = searchParams.get("reason") || "";
        enqueueSnackbar(
          t(
            connectFailureKeys[reason] ||
              "profile.microsoftConnectFailedGeneric",
          ),
          { variant: "error" },
        );
      }

      setSearchParams(
        (prev) => {
          prev.delete("ms-connected");
          prev.delete("reason");
          return prev;
        },
        { replace: true },
      );
    }
  }, [searchParams, setSearchParams, t]);

  const handleConnect = () => {
    connectMutation.mutate();
  };

  const handleDisconnect = () => {
    setDisconnectDialogOpen(true);
  };

  const handleConfirmDisconnect = (confirmed: boolean) => {
    if (confirmed) {
      disconnectMutation.mutate();
    }
    setDisconnectDialogOpen(false);
  };

  const isRevoked = !!microsoftStatus?.revoked;
  const isConnected = !!microsoftStatus?.connected && !isRevoked;

  const microsoftLogo = (
    <Box
      component="img"
      src="/microsoft_logo.png"
      alt=""
      sx={{ width: 20, height: 20 }}
    />
  );

  const connectedDetails = microsoftStatus && (
    <>
      {microsoftStatus.account_email && (
        <Box component="span" sx={{ display: "block" }}>
          {t("profile.microsoftConnectedAs", {
            email: microsoftStatus.account_email,
          })}
        </Box>
      )}
      {isConnected && microsoftStatus.connected_at && (
        <Box component="span" sx={{ display: "block" }}>
          {t("profile.microsoftConnectedSince", {
            date: formatDate(microsoftStatus.connected_at),
          })}
        </Box>
      )}
    </>
  );

  const connectButton = (
    <Button
      onClick={handleConnect}
      disabled={connectMutation.isPending}
      startIcon={
        connectMutation.isPending ? (
          <CircularProgress size={14} sx={{ color: "inherit" }} />
        ) : undefined
      }
      sx={profileButtonSx("primary")}
    >
      {isRevoked
        ? t("profile.microsoftReconnect")
        : t("profile.microsoftConnect")}
    </Button>
  );

  return (
    <>
      {isLoading || !microsoftStatus ? (
        <SettingRow
          icon={microsoftLogo}
          title={t("profile.microsoftTitle")}
          description={
            <CircularProgress size={16} sx={{ color: color.accent, mt: 0.5 }} />
          }
        />
      ) : isRevoked ? (
        <SettingRow
          tone="danger"
          icon={<WarningAmberRounded />}
          title={t("profile.microsoftRevokedTitle")}
          description={
            <>
              <Box component="span" sx={{ display: "block" }}>
                {t("profile.microsoftRevokedMessage")}
              </Box>
              {connectedDetails}
            </>
          }
          action={connectButton}
        />
      ) : isConnected ? (
        <SettingRow
          icon={microsoftLogo}
          title={t("profile.microsoftTitle")}
          description={connectedDetails}
          action={
            <Button
              onClick={handleDisconnect}
              disabled={disconnectMutation.isPending}
              startIcon={
                disconnectMutation.isPending ? (
                  <CircularProgress size={14} sx={{ color: "inherit" }} />
                ) : (
                  <LinkOffRounded />
                )
              }
              sx={profileButtonSx("outlined")}
            >
              {t("profile.microsoftDisconnect")}
            </Button>
          }
        />
      ) : (
        <SettingRow
          icon={microsoftLogo}
          title={t("profile.microsoftTitle")}
          description={t("profile.microsoftDescription")}
          action={connectButton}
        />
      )}

      <ConfirmationDialog
        open={disconnectDialogOpen}
        onClose={handleConfirmDisconnect}
        title={t("profile.microsoftDisconnectConfirmTitle")}
        description={t("profile.microsoftDisconnectConfirmMessage")}
        content={t("profile.microsoftDisconnectConfirmMessage")}
        actions={[
          {
            label: t("profile.microsoftDisconnectConfirmNo"),
            onClick: () => handleConfirmDisconnect(false),
          },
          {
            label: t("profile.microsoftDisconnectConfirmYes"),
            onClick: () => handleConfirmDisconnect(true),
          },
        ]}
      />
    </>
  );
};

export default MicrosoftConnection;
